import {
  OrderSummaryBodyServer1,
  OrderSummaryBodyServer2,
  OrderSummaryBodyServer3,
  OrderSummaryBodyServer4,
  PermanentProxy,
  ProxyOrderSummaryResponse,
  Server3UpdateOptionsResponse,
  ProxyServer,
  PurchaseProxyRequest,
  Server1Options,
  Server2Options,
  Server3Options,
  Server4Options,
  Server1NextOptions,
  Server2NextOptions,
  Server3NextOptions,
  Server4NextOptions,
  Server4ParentProxiesResponse,
  Server4ProvidersResponse,
  CheckUsername5gResponse,
  ModifyProxy5gBody,
  AddIpToProxy5gBody,
  RemoveIpFromProxy5gBody,
  ChangePackage5gBody,
  Proxy5gFullDetails,
} from '../../types/api';
import { baseApi } from './baseApi';

// Get Available Proxy Servers
export interface GetProxyServersResponse {
  status: 'success';
  message: string;
  data: {
    servers: ProxyServer[];
  };
}

// Get Server Options
export type GetServerOptionsResponse =
  | Server1Options
  | Server2Options
  | Server3Options
  | Server4Options;

// Get Next Options
export type GetServerNextOptionsResponse =
  | Server1NextOptions
  | Server2NextOptions
  | Server3NextOptions
  | Server4NextOptions;

// Purchase Proxy
export interface PurchaseProxyResponse {
  status: 'success';
  message: string;
  data: {
    proxies: PermanentProxy[];
  };
}

// Get Proxies List
export interface GetProxiesParams {
  status?: 'active' | 'expired' | 'cancelled';
  serverId?: 1 | 2 | 3 | 4;
  page?: number;
  limit?: number;
}

export interface GetProxiesResponse {
  status: 'success';
  message: string;
  data: {
    proxies: PermanentProxy[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

// Get Proxy Details
export interface GetProxyDetailsResponse {
  status: 'success';
  message: string;
  data: {
    proxy: PermanentProxy;
  };
}

export interface ProxyActionResponse {
  status: 'success';
  message: string;
  data: {
    action: string;
    serverId: number;
    period?: number;
    proxy: PermanentProxy;
  };
}

export interface ExtendPriceResponse {
  status: 'success';
  message: string;
  data: {
    price: number;
    currency: string;
  };
}

export type UpdateServer3ProxyBody =
  | { action: 'change_region'; regionCode: string }
  | { action: 'change_country'; countryCode: string }
  | { action: 'change_carrier'; carrier: string }
  | { action: 'change_proxy_type'; proxyType: 'SOCKS5' | 'HTTPS' }
  | { action: 'toggle_rotation' };

// Public endpoint for disabled servers
export interface GetDisabledServersPublicResponse {
  status: 'success';
  data: {
    disabledServers: string[];
  };
}

export const proxiesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProxyServers: builder.query<GetProxyServersResponse, void>({
      query: () => ({
        url: '/proxies/servers',
        method: 'GET',
      }),
    }),
    getDisabledServers: builder.query<GetDisabledServersPublicResponse, void>({
      query: () => ({
        url: '/servers/disabled',
        method: 'GET',
      }),
    }),
    getServerOptions: builder.query<GetServerOptionsResponse, number>({
      query: (serverId) => ({
        url: `/proxies/servers/${serverId}/options`,
        method: 'GET',
      }),
    }),
    getServerNextOptions: builder.query<
      GetServerNextOptionsResponse,
      { serverId: number; params: Record<string, string> }
    >({
      query: ({ serverId, params }) => {
        const queryString = new URLSearchParams(params).toString();
        return {
          url: `/proxies/servers/${serverId}/options/next${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
    }),
    purchaseProxy: builder.mutation<PurchaseProxyResponse, PurchaseProxyRequest>({
      query: (data) => ({
        url: '/proxies/purchase',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Proxies', 'Balance', 'Transactions'],
    }),
    getProxies: builder.query<GetProxiesResponse, GetProxiesParams>({
      query: ({ status, serverId, page = 1, limit = 20 }) => {
        const params = new URLSearchParams();
        if (status) params.append('status', status);
        if (serverId) params.append('serverId', serverId.toString());
        if (page) params.append('page', page.toString());
        if (limit) params.append('limit', limit.toString());

        const queryString = params.toString();
        return {
          url: `/proxies${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      providesTags: ['Proxies'],
    }),
    getProxyDetails: builder.query<GetProxyDetailsResponse, string>({
      query: (proxyId) => ({
        url: `/proxies/${proxyId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, proxyId) => [{ type: 'Proxy', id: proxyId }],
    }),
    getServer3UpdateOptions: builder.query<Server3UpdateOptionsResponse, string>({
      query: (proxyId) => ({
        url: `/proxies/${proxyId}/server3/options`,
        method: 'GET',
      }),
      providesTags: (_result, _error, proxyId) => [{ type: 'Proxy', id: proxyId }],
    }),
    renewProxy: builder.mutation<ProxyActionResponse, { proxyId: string; days?: number; months?: number; hours?: number }>({
      query: ({ proxyId, days, months, hours }) => {
        const body: Record<string, number> = {};
        if (typeof days === 'number') body.days = days;
        if (typeof months === 'number') body.months = months;
        if (typeof hours === 'number') body.hours = hours;
        return {
          url: `/proxies/${proxyId}/renew`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags: (_result, _error, { proxyId }) => [{ type: 'Proxy', id: proxyId }, 'Proxies', 'Balance', 'Transactions'],
    }),
    getProxyExtendPrice: builder.query<ExtendPriceResponse, { proxyId: string; months: number }>({
      query: ({ proxyId, months }) => ({
        url: `/proxies/${proxyId}/extend/price`,
        method: 'POST',
        body: { months },
      }),
    }),
    getProxy5gExtendPrice: builder.query<ExtendPriceResponse, { proxyId: string; days: string | number }>({
      query: ({ proxyId, days }) => ({
        url: `/proxies/${proxyId}/extend/price`,
        method: 'POST',
        body: { days },
      }),
    }),
    updateServer3Proxy: builder.mutation<
      ProxyActionResponse,
      { proxyId: string; body: UpdateServer3ProxyBody }
    >({
      query: ({ proxyId, body }) => ({
        url: `/proxies/${proxyId}/server3`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { proxyId }) => [{ type: 'Proxy', id: proxyId }, 'Proxies'],
    }),
    /** Get available parent proxies for Server 4 (5G) */
    getServer4ParentProxies: builder.query<
      Server4ParentProxiesResponse,
      { pkgId: number; countryId: number; cityId?: number; serviceProviderCityId?: number }
    >({
      query: ({ pkgId, countryId, cityId, serviceProviderCityId }) => {
        const params = new URLSearchParams({ pkgId: pkgId.toString(), countryId: countryId.toString() });
        if (cityId) params.append('cityId', cityId.toString());
        if (serviceProviderCityId) params.append('serviceProviderCityId', serviceProviderCityId.toString());
        return {
          url: `/proxies/servers/4/parent-proxies?${params.toString()}`,
          method: 'GET',
        };
      },
    }),
    /** Get service providers for Server 4 (5G) based on city and package. */
    getServer4Providers: builder.query<Server4ProvidersResponse, { serverId: number; cityId: number; pkgId: number }>({
      query: ({ serverId, cityId, pkgId }) => ({
        url: `/proxies/servers/${serverId}/providers?cityId=${cityId}&packageId=${pkgId}`,
        method: 'GET',
      }),
    }),
    /** Get parent proxies for Server 4 (5G) filtered by service provider. */
    getServer4Parents: builder.query<Server4ParentProxiesResponse, { serverId: number; serviceProviderCityId: number; pkgId: number; countryId: number; cityId?: number }>({
      query: ({ serverId, serviceProviderCityId, pkgId, countryId, cityId }) => {
        const params = new URLSearchParams({
          serviceProviderCityId: serviceProviderCityId.toString(),
          packageId: pkgId.toString(),
          countryId: countryId.toString(),
        });
        if (cityId) params.append('cityId', cityId.toString());
        return {
          url: `/proxies/servers/${serverId}/parents?${params.toString()}`,
          method: 'GET',
        };
      },
    }),
    /** Order summary (price) before purchase. Server 1: { serviceId, orderConfig }. Server 2: { type, ipVersion, country, period, quantity }. Server 3: { proxyCount, days, proxyType, regionCode?|countryCode?, carrier?, rotation? }. Server 4: { pkgId, countryId, cityId, parentProxyId, protocol, duration }. */
    checkProxy5gUsername: builder.query<CheckUsername5gResponse, { username: string; pkgId: number }>({
      query: ({ username, pkgId }) => ({
        url: `/proxies/5g/check-username?username=${encodeURIComponent(username)}&pkgId=${pkgId}`,
        method: 'GET',
      }),
      keepUnusedDataFor: 0,
    }),
    validateParentProxy: builder.query<{ data: { isAvailable: boolean; details: Record<string, unknown> | null } }, { parentProxyId: number | string; pkgId?: number }>({
      query: ({ parentProxyId, pkgId }) => ({
        url: `/proxies/5g/validate-parent-proxy?parentProxyId=${parentProxyId}${pkgId ? `&pkgId=${pkgId}` : ''}`,
        method: 'GET',
      }),
    }),
    modifyProxy5g: builder.mutation<ProxyActionResponse, { proxyId: string; body: ModifyProxy5gBody }>({
      query: ({ proxyId, body }) => ({
        url: `/proxies/${proxyId}/5g`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { proxyId }) => [{ type: 'Proxy', id: proxyId }, 'Proxies', 'Proxy5gDetails'],
    }),
    addIpToProxy5g: builder.mutation<ProxyActionResponse, { proxyId: string; body: AddIpToProxy5gBody }>({
      query: ({ proxyId, body }) => ({
        url: `/proxies/${proxyId}/5g/add-ip`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { proxyId }) => [{ type: 'Proxy', id: proxyId }, 'Proxies', 'Proxy5gDetails'],
    }),
    removeIpFromProxy5g: builder.mutation<ProxyActionResponse, { proxyId: string; body: RemoveIpFromProxy5gBody }>({
      query: ({ proxyId, body }) => ({
        url: `/proxies/${proxyId}/5g/remove-ip`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { proxyId }) => [{ type: 'Proxy', id: proxyId }, 'Proxies', 'Proxy5gDetails'],
    }),
    changeProxy5gPackage: builder.mutation<ProxyActionResponse, { proxyId: string; body: ChangePackage5gBody }>({
      query: ({ proxyId, body }) => ({
        url: `/proxies/${proxyId}/5g/change-package`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { proxyId }) => [{ type: 'Proxy', id: proxyId }, 'Proxies', 'Proxy5gDetails'],
    }),
    getFullProxy5gDetails: builder.query<{ status: string; data: Proxy5gFullDetails }, string>({
      query: (proxyId) => ({
        url: `/proxies/${proxyId}/5g/details`,
        method: 'GET',
      }),
      providesTags: ['Proxy5gDetails'],
    }),
    getGbPackages: builder.query<{ status: string; data: { packages: any[] } }, void>({
      query: () => ({ url: "/proxies/gb/packages", method: "GET" }),
      providesTags: ["Proxies"],
    }),
    purchaseGbPackage: builder.mutation<any, { tariffId: number; title?: string; packageMongoId?: string | null }>({
      query: (body) => ({ url: "/proxies/gb/packages", method: "POST", body }),
      invalidatesTags: ["Proxies", "Balance", "Transactions"],
    }),
    gbPackageAction: builder.mutation<any, { packageId: string; action: string; body?: Record<string, unknown>; method?: "GET" | "POST" }>({
      query: ({ packageId, action, body, method }) => ({
        url: `/proxies/gb/packages/${packageId}/${action}`,
        method: method || "POST",
        body: method === "GET" ? undefined : (body || {}),
      }),
      invalidatesTags: ["Proxies", "Balance", "Transactions"],
    }),
    gbProxyAction: builder.mutation<any, { proxyId: string; action: string; body?: Record<string, unknown> }>({
      query: ({ proxyId, action, body }) => ({
        url: `/proxies/gb/proxies/${proxyId}/${action}`,
        method: "POST",
        body: body || {},
      }),
      invalidatesTags: ["Proxies"],
    }),
    getOrderSummary: builder.mutation<
      ProxyOrderSummaryResponse,
      | { serverId: 1; body: OrderSummaryBodyServer1 }
      | { serverId: 2; body: OrderSummaryBodyServer2 }
      | { serverId: 3; body: OrderSummaryBodyServer3 }
      | { serverId: 4; body: OrderSummaryBodyServer4 }
    >({
      query: ({ serverId, body }) => ({
        url: `/proxies/servers/${serverId}/summary`,
        method: 'POST',
        body,
      }),
    }),
  }),
});

export const {
  useGetProxyServersQuery,
  useGetDisabledServersQuery,
  useGetServerOptionsQuery,
  useGetServerNextOptionsQuery,
  usePurchaseProxyMutation,
  useGetGbPackagesQuery,
  usePurchaseGbPackageMutation,
  useGbPackageActionMutation,
  useGbProxyActionMutation,
  useGetProxiesQuery,
  useGetProxyDetailsQuery,
  useGetServer3UpdateOptionsQuery,
  useGetServer4ParentProxiesQuery,
  useRenewProxyMutation,
  useUpdateServer3ProxyMutation,
  useGetOrderSummaryMutation,
  useGetProxyExtendPriceQuery,
  useGetProxy5gExtendPriceQuery,
  useCheckProxy5gUsernameQuery,
  useLazyCheckProxy5gUsernameQuery,
  useValidateParentProxyQuery,
  useModifyProxy5gMutation,
  useAddIpToProxy5gMutation,
  useRemoveIpFromProxy5gMutation,
  useChangeProxy5gPackageMutation,
  useGetFullProxy5gDetailsQuery,
  useLazyGetFullProxy5gDetailsQuery,
  useGetServer4ProvidersQuery,
  useGetServer4ParentsQuery,
} = proxiesApi;

